import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
  Chip,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AutoAwesome as AgentsIcon,
  People as UsersIcon,
  ViewKanban as StatusIcon,
  Category as SegmentsIcon,
  AccountBalance as FinanceiroIcon,
  AdminPanelSettings as SuperAdminIcon,
  Logout as LogoutIcon,
  DarkMode,
  LightMode,
  History as HistoryIcon,
  AccessTime as TimeIcon,
  Assessment as ReportsIcon,
  PermMedia as MediaIcon,
  Settings as SettingsIcon,
  Lightbulb as NovidadesIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isWhiteLabelRole } from '../utils/roleMapping';
import { useThemeMode } from '../contexts/ThemeContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useTrialDays } from '../hooks/useTrialDays';
import { useWhiteLabelHiding } from '../hooks/useWhiteLabelHiding';
import AgentsManagement from '../components/admin/AgentsManagement';
import AdminOverview from '../components/admin/AdminOverview';
import UsersManagement from '../components/admin/UsersManagement';
import SegmentsManagement from '../components/admin/SegmentsManagement';
import StatusManagement from '../components/admin/StatusManagement';
import FinanceiroManagement from '../components/admin/FinanceiroManagement';
import MediaManagement from '../components/admin/MediaManagement';
import OrganizationSettings from '../components/admin/OrganizationSettings';
import ProtectedAdminLanding from '../components/admin/ProtectedAdminLanding';
import SuperAdminPage from './SuperAdminPage';
import CreditosPage from './super-admin/CreditosPage';
import ContasPage from './super-admin/ContasPage';
import ConfiguracoesPage from './super-admin/ConfiguracoesPage';
import TemplatesPage from './super-admin/TemplatesPage';
import NovidadesPage from './super-admin/NovidadesPage';
import NovidadesViewPage from './NovidadesViewPage';
import ReportsPage from './ReportsPage';
import CreditHistoryPage from './CreditHistoryPage';
import CollapsibleSidebar from '../components/admin/CollapsibleSidebar';
import { AccountOption } from '../components/admin/AccountSwitchDialog';
import TrialModal from '../components/TrialModal';
import PlanInactiveModal from '../components/PlanInactiveModal';
import PlanInactiveAlert from '../components/PlanInactiveAlert';
import FirstTimeWelcomeModal from '../components/FirstTimeWelcomeModal';
import SupportChatWidget from '../components/SupportChatWidget';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from 'react-i18next';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, isAuthenticated } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const { trialData, loading: trialLoading } = useTrialDays();
  const { canViewMenu, canShowAlert, canCreateAgents } = usePermissions();
  const { shouldHidePaymentElements } = useWhiteLabelHiding();
  const { t } = useTranslation();
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [planInactiveModalOpen, setPlanInactiveModalOpen] = useState(false);
  const [planInactiveAlertOpen, setPlanInactiveAlertOpen] = useState(false);
  const [firstTimeWelcomeOpen, setFirstTimeWelcomeOpen] = useState(false);
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { currentOrganization, availableOrganizations, switchOrganization } = useAuth();

  // Mostrar modal de boas-vindas no primeiro acesso
  useEffect(() => {
    const shouldShowWelcome = (location.state as any)?.showWelcome;
    const hasSeenFirstTimeWelcome = localStorage.getItem('hasSeenFirstTimeWelcome');

    if (shouldShowWelcome && !hasSeenFirstTimeWelcome && !shouldHidePaymentElements) {
      setFirstTimeWelcomeOpen(true);
      localStorage.setItem('hasSeenFirstTimeWelcome', 'true');
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname, shouldHidePaymentElements]);

  // Mostrar modal de teste no primeiro login
  useEffect(() => {
    const hasSeenTrialModal = localStorage.getItem('hasSeenTrialModal');
    if (!hasSeenTrialModal && trialData && trialData.isTrialActive && canShowAlert('showTrialAlerts') && !shouldHidePaymentElements) {
      setTrialModalOpen(true);
      localStorage.setItem('hasSeenTrialModal', 'true');
    }
  }, [trialData, canShowAlert, shouldHidePaymentElements]);

  // // Verificar se o plano está inativo
  // useEffect(() => {
  //   if (user && user.isPlanActive === false && canShowAlert('showPlanInactiveAlerts') && !shouldHidePaymentElements) {
  //     setPlanInactiveAlertOpen(true);
  //     // Mostrar modal apenas uma vez por login
  //     const hasSeenPlanModal = localStorage.getItem('hasSeenPlanInactiveModal');
  //     if (!hasSeenPlanModal && canShowAlert('showPaymentAlerts')) {
  //       setPlanInactiveModalOpen(true);
  //       localStorage.setItem('hasSeenPlanInactiveModal', 'true');
  //     }
  //   } else if (user && user.isPlanActive === true) {
  //     // Se o plano estiver ativo, esconder os alertas
  //     setPlanInactiveAlertOpen(false);
  //     setPlanInactiveModalOpen(false);
  //   }
  // }, [user, isAuthenticated, canShowAlert, shouldHidePaymentElements]);

  const handleOrganizationSwitch = async (organizationId: string) => {
    const success = await switchOrganization(organizationId);
    if (!success) {
      console.error('Failed to switch organization');
    }
  };

  const menuItems = [
    // Filtrar itens de menu baseado nas permissões
    ...(canViewMenu('canViewDashboard') ? [{
      text: t('admin.overview'),
      icon: <DashboardIcon />,
      path: '/admin/overview',
      category: t('sidebar.categories.dashboard')
    }] : []),
    ...(canViewMenu('canViewAgents') ? [{
      text: t('admin.agents'),
      icon: <AgentsIcon />,
      path: '/admin/agents',
      category: t('sidebar.categories.management')
    }] : []),
    ...(canViewMenu('canViewUsers') ? [{
      text: t('admin.users'),
      icon: <UsersIcon />,
      path: '/admin/users',
      category: t('sidebar.categories.management')
    }] : []),
    ...(canViewMenu('canViewSegments') ? [{
      text: t('admin.segments'),
      icon: <SegmentsIcon />,
      path: '/admin/segments',
      category: t('sidebar.categories.management')
    }] : []),
    // ...(canViewMenu('canViewFinanceiro') && !shouldHidePaymentElements ? [{
    //   text: t('admin.financial'),
    //   icon: <FinanceiroIcon />,
    //   path: '/admin/financeiro',
    //   category: t('sidebar.categories.management')
    // }] : []),
    ...(canViewMenu('canViewCredits') ? [{
      text: t('admin.creditHistory'),
      icon: <HistoryIcon />,
      path: '/admin/credit-history',
      category: t('sidebar.categories.management')
    }] : []),
    ...(canViewMenu('canViewSettings') ? [{
      text: t('admin.status'),
      icon: <StatusIcon />,
      path: '/admin/status',
      category: t('sidebar.categories.settings')
    }] : []),
    ...(canViewMenu('canViewSettings') ? [{
      text: t('admin.media'),
      icon: <MediaIcon />,
      path: '/admin/media',
      category: t('sidebar.categories.settings')
    }] : []),
    ...(canViewMenu('canViewReports') ? [{
      text: t('admin.reports'),
      icon: <ReportsIcon />,
      path: '/admin/reports',
      category: t('sidebar.categories.analysis')
    }] : []),
    // Super Admin menu item - only visible for Super Admins
    ...(user?.isSuperAdmin ? [{
      text: t('admin.superAdmin'),
      icon: <SuperAdminIcon />,
      path: '/admin/super-admin',
      category: t('sidebar.categories.administration')
    }] : []),
    // WhiteLabel menu item - only visible for WhiteLabel users
    ...(user?.isWhiteLabel || user?.role === 'whitelabel' ? [{
      text: t('admin.whiteLabel'),
      icon: <SuperAdminIcon />, // Usar o mesmo ícone por enquanto
      path: '/admin/whitelabel',
      category: t('sidebar.categories.administration')
    }] : [])
  ];

  const handleNavigateToNormalDashboard = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const isActiveRoute = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleSelectAccount = (accountId: string) => {
    handleOrganizationSwitch(accountId);
  };

  const handleGoToPlans = () => {
    navigate('/change-plan');
    setPlanInactiveModalOpen(false);
  };

  const handleClosePlanInactiveAlert = () => {
    setPlanInactiveAlertOpen(false);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100vh', 
      bgcolor: 'background.default',
      overflow: 'hidden',
      width: '100vw',
      maxWidth: '100vw'
    }}>
      {/* Sidebar para desktop */}
      {!isMobile && (
        <Paper
          elevation={3}
          sx={{
            flexShrink: 0,
            borderRadius: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <CollapsibleSidebar
            menuItems={menuItems}
            selectedAccount={currentOrganization ? { id: currentOrganization.id, name: currentOrganization.name } : null}
            accounts={availableOrganizations.map(org => ({ id: org.id, name: org.name }))}
            onSelectAccount={handleSelectAccount}
            onNavigateToNormalDashboard={handleNavigateToNormalDashboard}
            onLogout={handleLogout}
            onMenuItemClick={handleMenuItemClick}
            isActiveRoute={isActiveRoute}
            isMobile={false}
            showAccountSwitcher={user?.isSuperAdmin || user?.isWhiteLabel || user?.role === 'whitelabel' || false}
            planInactiveAlertOpen={planInactiveAlertOpen}
          />
        </Paper>
      )}

      {/* Drawer para mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          display: { md: 'none' },
          '& .MuiDrawer-paper': {
            overflow: 'hidden',
          }
        }}
      >
        <CollapsibleSidebar
          menuItems={menuItems}
          selectedAccount={currentOrganization ? { id: currentOrganization.id, name: currentOrganization.name } : null}
          accounts={availableOrganizations.map(org => ({ id: org.id, name: org.name }))}
          onSelectAccount={handleSelectAccount}
          onNavigateToNormalDashboard={handleNavigateToNormalDashboard}
          onLogout={handleLogout}
          onMenuItemClick={handleMenuItemClick}
          isActiveRoute={isActiveRoute}
          isMobile={true}
          onClose={toggleDrawer}
          showAccountSwitcher={user?.isSuperAdmin || user?.isWhiteLabel || user?.role === 'whitelabel' || false}
          planInactiveAlertOpen={planInactiveAlertOpen}
        />
      </Drawer>

      {/* Conteúdo principal */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}>
        {/* Header */}
        <AppBar
          position="static"
          elevation={1}
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Toolbar sx={{ 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            py: isMobile ? 1 : 0,
            gap: isMobile ? 1 : 0
          }}>
            {/* Primeira linha - Título e menu mobile */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              width: '100%',
              justifyContent: isMobile ? 'space-between' : 'flex-start'
            }}>
              {isMobile && (
                <IconButton
                  edge="start"
                  onClick={toggleDrawer}
                  sx={{ mr: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: isMobile ? '1rem' : '1.25rem',
                  flexGrow: isMobile ? 0 : 1,
                  textAlign: isMobile ? 'center' : 'left',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {t('admin.title')}
              </Typography>
            </Box>

            {/* Segunda linha - Conteúdo e ações (apenas em mobile) */}
            {isMobile && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%',
                gap: 1
              }}>
                {/* Lado esquerdo - Bem-vindo e trial */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    {t('dashboard.welcome')}, <strong>{user?.name}</strong>
                  </Typography>

                  {/* Trial data simplificado */}
                  {trialData && trialData.isTrialActive && canShowAlert('showTrialAlerts') && (
                    <Chip
                      icon={<TimeIcon sx={{ fontSize: '0.8rem' }} />}
                      label={`${trialData.daysRemaining}d`}
                      color="warning"
                      variant="outlined"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: '24px',
                        '& .MuiChip-icon': {
                          color: 'warning.main',
                        },
                        '& .MuiChip-label': {
                          px: 0.5
                        }
                      }}
                    />
                  )}
                </Box>

                {/* Lado direito - Botões de ação */}
                <Box sx={{ display: 'flex', alignItems: 'center', '& > *:not(:first-of-type)': {
                    marginLeft: -0.70
                  },
                }}>
                  <IconButton
                    color="inherit"
                    onClick={toggleMode}
                    title={mode === 'dark' ? t('dashboard.toggleLightMode') : t('dashboard.toggleDarkMode')}
                    aria-label={mode === 'dark' ? t('dashboard.toggleLightMode') : t('dashboard.toggleDarkMode')}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover',
                        transform: 'scale(1.1)',
                      },
                      '&:focus': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
                  </IconButton>
                  <LanguageSelector />
                  <Tooltip title="Novidades e Sugestões">
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => navigate('/admin/novidades')}
                      sx={{ 
                        color: 'text.secondary',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'action.hover',
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <NovidadesIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    color="inherit"
                    onClick={handleLogout}
                    title={t('common.logout')}
                    aria-label={t('common.logout')}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: 'error.main',
                        backgroundColor: 'error.light',
                        transform: 'scale(1.1)',
                      },
                      '&:focus': {
                        outline: '2px solid',
                        outlineColor: 'error.main',
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <LogoutIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Layout desktop - mantém o original */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {t('dashboard.welcome')}, <strong>{user?.name}</strong>
                </Typography>

                {/* Exibir dias restantes de teste */}
                {trialData && trialData.isTrialActive && canShowAlert('showTrialAlerts') && (
                  <Chip
                    icon={<TimeIcon />}
                    label={`${trialData.daysRemaining} ${t('dashboard.daysRemaining')}`}
                    color="warning"
                    variant="outlined"
                    sx={{
                      fontWeight: 600,
                      '& .MuiChip-icon': {
                        color: 'warning.main',
                      },
                    }}
                  />
                )}
                <IconButton
                  color="inherit"
                  onClick={toggleMode}
                  title={mode === 'dark' ? t('dashboard.toggleLightMode') : t('dashboard.toggleDarkMode')}
                  aria-label={mode === 'dark' ? t('dashboard.toggleLightMode') : t('dashboard.toggleDarkMode')}
                  sx={{
                    color: 'text.secondary',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'action.hover',
                      transform: 'scale(1.1)',
                    },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  {mode === 'dark' ? <LightMode /> : <DarkMode />}
                </IconButton>
                <LanguageSelector />
                <Tooltip title="Novidades e Sugestões">
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/admin/novidades')}
                    sx={{ 
                      color: 'text.secondary',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <NovidadesIcon />
                  </IconButton>
                </Tooltip>
                <IconButton
                  color="inherit"
                  onClick={handleLogout}
                  title={t('common.logout')}
                  aria-label={t('common.logout')}
                  sx={{
                    color: 'text.secondary',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: 'error.main',
                      backgroundColor: 'error.light',
                      transform: 'scale(1.1)',
                    },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'error.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </AppBar>

        {/* Área de conteúdo */}
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          background: mode === 'light' ? '#f2f2f2' : 'transparent',
          width: '100%',
          maxWidth: '100%'
        }}>
          <Container maxWidth="xl" sx={{ 
            py: 3,
            width: '100%',
            maxWidth: '100%',
            px: { xs: 1, sm: 2, md: 3 }
          }}>
            {/* Alerta de plano inativo */}
            {/* {planInactiveAlertOpen && user?.isPlanActive === false && canShowAlert('showPlanInactiveAlerts') && (
              <PlanInactiveAlert
                onClose={handleClosePlanInactiveAlert}
                onGoToPlans={handleGoToPlans}
              />
            )} */}
            <Routes>
              <Route path="/" element={<ProtectedAdminLanding />} />
              {canViewMenu('canViewDashboard') && (
                <Route path="/overview" element={<AdminOverview />} />
              )}
              {canViewMenu('canViewAgents') && (
                <Route path="/agents/*" element={<AgentsManagement />} />
              )}
              {canViewMenu('canViewUsers') && (
                <Route path="/users/*" element={<UsersManagement />} />
              )}
              {canViewMenu('canViewSegments') && (
                <Route path="/segments/*" element={<SegmentsManagement />} />
              )}
              {/* {canViewMenu('canViewFinanceiro') && (
                <Route path="/financeiro" element={<FinanceiroManagement />} />
              )} */}
              {canViewMenu('canViewSettings') && (
                <Route path="/status/*" element={<StatusManagement />} />
              )}
              {canViewMenu('canViewSettings') && (
                <Route path="/media" element={<MediaManagement />} />
              )}
              {canViewMenu('canViewReports') && (
                <Route path="/reports" element={<ReportsPage />} />
              )}
              {/* Super Admin routes - protected */}
              {user?.isSuperAdmin && (
                <>
                  <Route path="/super-admin" element={<SuperAdminPage />} />
                  <Route path="/super-admin/creditos" element={<CreditosPage />} />
                  <Route path="/super-admin/contas/*" element={<ContasPage />} />
                  <Route path="/super-admin/configuracoes" element={<ConfiguracoesPage />} />
                  <Route path="/super-admin/templates" element={<TemplatesPage />} />
                  <Route path="/super-admin/novidades" element={<NovidadesPage />} />
                </>
              )}
              {/* WhiteLabel routes - protected */}
              {(user?.isWhiteLabel || user?.role === 'whitelabel') && (
                <>
                  <Route path="/whitelabel" element={<SuperAdminPage />} />
                  <Route path="/whitelabel/creditos" element={<CreditosPage />} />
                  <Route path="/whitelabel/contas/*" element={<ContasPage />} />
                  <Route path="/whitelabel/configuracoes" element={<OrganizationSettings />} />
                </>
              )}
              {canViewMenu('canViewCredits') && (
                <Route path="/credit-history" element={<CreditHistoryPage />} />
              )}
              <Route path="/novidades" element={<NovidadesViewPage />} />
            </Routes>
          </Container>
        </Box>

        {/* Modal de plano inativo */}
        {/* {canShowAlert('showPaymentAlerts') && (
          <PlanInactiveModal
            open={planInactiveModalOpen}
            onClose={() => setPlanInactiveModalOpen(false)}
            onGoToPlans={handleGoToPlans}
          />
        )} */}

        {/* Modal de boas-vindas para primeiro acesso */}
        {!shouldHidePaymentElements && (
          <FirstTimeWelcomeModal
            open={firstTimeWelcomeOpen}
            onClose={() => setFirstTimeWelcomeOpen(false)}
          />
        )}

        {/* Modal de trial */}
        {/* {!shouldHidePaymentElements && (
          <TrialModal
            open={trialModalOpen}
            onClose={() => setTrialModalOpen(false)}
            daysRemaining={trialData?.daysRemaining || 0}
          />
        )} */}

        {/* Widget de chat de suporte - não aparece para usuários WhiteLabel */}
        {!isWhiteLabelRole(user?.role || '') && (
          <SupportChatWidget
            isOpen={supportChatOpen}
            onToggle={() => setSupportChatOpen(!supportChatOpen)}
          />
        )}
      </Box>
    </Box>
  );
};

export default AdminDashboard;